import { useEffect, useMemo, useState } from "react";
import { Images, Download, Search, Users, UserCheck } from "lucide-react";
import api, { getToken } from "@/lib/api";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin Photo Library
 * --------------------
 * Renders every photo the system has captured — staff (from CampMinder
 * enrich + late-arrival uploads) and visitor sign-ins — with per-row
 * downloads and a bulk ZIP export. Lives under Settings → Photo Library.
 */
export default function PhotoLibraryPanel() {
  const [kind, setKind] = useState("staff"); // "staff" | "visitor"
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [downloadingZip, setDownloadingZip] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/photos?kind=${kind}`);
      setItems(r.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load photos");
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [kind]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((x) => (x.name || "").toLowerCase().includes(needle));
  }, [q, items]);

  // Build an authenticated GET that streams a blob and forces a download.
  const fetchAndDownload = async (url, suggestedFilename) => {
    const token = getToken();
    const r = await fetch(`${API}${url}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const blob = await r.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = suggestedFilename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  };

  const downloadOne = async (item) => {
    try {
      const stem = (item.name || "photo").trim().replace(/\s+/g, "_");
      await fetchAndDownload(`/api/admin/photos/${item.kind}/${encodeURIComponent(item.id)}`,
                             `${stem}.jpg`);
    } catch (e) {
      toast.error(`Download failed: ${e.message}`);
    }
  };

  const downloadAllZip = async () => {
    setDownloadingZip(true);
    try {
      await fetchAndDownload(`/api/admin/photos/download.zip?kind=${kind}`,
                             `rrdc-photos-${kind}.zip`);
      toast.success("ZIP download started.");
    } catch (e) {
      toast.error(`ZIP download failed: ${e.message}`);
    } finally {
      setDownloadingZip(false);
    }
  };

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6 lg:col-span-2"
             data-testid="photo-library-panel">
      <div className="flex items-center gap-2 mb-1">
        <Images className="w-4 h-4 text-indigo-600" />
        <h2 className="font-display text-lg font-bold">Photo Library</h2>
      </div>
      <p className="text-sm text-slate-500 mb-4">
        Every face on file — staff (CampMinder roster + late arrivals) and visitors (kiosk sign-ins).
        Click a card to download a single photo, or grab the whole roster as a ZIP.
      </p>

      <div className="flex flex-wrap items-center gap-2 mb-4">
        <div className="inline-flex rounded-xl border border-slate-200 overflow-hidden">
          <button
            onClick={() => setKind("staff")}
            className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${
              kind === "staff" ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="photo-tab-staff"
          >
            <UserCheck className="w-4 h-4" /> Staff
          </button>
          <button
            onClick={() => setKind("visitor")}
            className={`px-3 py-2 text-sm font-bold inline-flex items-center gap-1.5 ${
              kind === "visitor" ? "bg-indigo-600 text-white" : "bg-white text-slate-700 hover:bg-slate-50"
            }`}
            data-testid="photo-tab-visitor"
          >
            <Users className="w-4 h-4" /> Visitors
          </button>
        </div>

        <div className="flex-1 min-w-[180px] relative">
          <Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400" />
          <input
            type="search"
            placeholder="Search by name…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-300 text-sm"
            data-testid="photo-search"
          />
        </div>

        <button
          onClick={downloadAllZip}
          disabled={downloadingZip || items.length === 0}
          className="btn-touch px-4 rounded-xl bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 text-white font-semibold inline-flex items-center gap-2"
          data-testid="photo-download-zip"
        >
          <Download className="w-4 h-4" />
          {downloadingZip ? "Building ZIP…" : `Download ${kind === "staff" ? "Staff" : "Visitor"} ZIP`}
        </button>
      </div>

      {loading ? (
        <div className="text-slate-500 text-sm py-8 text-center">Loading photos…</div>
      ) : filtered.length === 0 ? (
        <div className="text-slate-500 text-sm py-8 text-center" data-testid="photo-empty">
          {q ? "No photos match your search." : "No photos on file yet."}
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3"
             data-testid="photo-grid">
          {filtered.map((item) => (
            <PhotoCard key={`${item.kind}_${item.id}`} item={item} onDownload={() => downloadOne(item)} />
          ))}
        </div>
      )}

      {!loading && filtered.length > 0 && (
        <div className="text-xs text-slate-400 mt-3">
          {filtered.length} {filtered.length === 1 ? "photo" : "photos"}
          {q && ` (filtered from ${items.length})`}
        </div>
      )}
    </section>
  );
}

function PhotoCard({ item, onDownload }) {
  const [src, setSrc] = useState(null);
  const [errored, setErrored] = useState(false);

  useEffect(() => {
    let revoked = false;
    let blobUrl = null;
    const token = getToken();
    fetch(`${API}/api/admin/photos/${item.kind}/${encodeURIComponent(item.id)}/thumb`, {
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.blob(); })
      .then((blob) => {
        if (revoked) return;
        blobUrl = URL.createObjectURL(blob);
        setSrc(blobUrl);
      })
      .catch(() => setErrored(true));
    return () => {
      revoked = true;
      if (blobUrl) URL.revokeObjectURL(blobUrl);
    };
  }, [item.kind, item.id]);

  return (
    <div className="rounded-xl border border-slate-200 bg-slate-50 overflow-hidden flex flex-col"
         data-testid={`photo-card-${item.kind}-${item.id}`}>
      <div className="aspect-square bg-slate-200 grid place-items-center text-slate-400 text-xs relative">
        {errored ? (
          "no photo"
        ) : src ? (
          <img src={src} alt={item.name || ""} className="w-full h-full object-cover" />
        ) : (
          "loading…"
        )}
      </div>
      <div className="px-2 py-2 flex flex-col gap-1">
        <div className="text-xs font-bold text-slate-900 truncate" title={item.name}>
          {item.name || "—"}
        </div>
        <div className="text-[10px] text-slate-500 truncate">
          {item.kind === "staff"
            ? [item.division, item.position].filter(Boolean).join(" · ") || "Staff"
            : item.phone_display || "Visitor"}
        </div>
        <button
          onClick={onDownload}
          className="mt-1 px-2 py-1 rounded-md bg-white border border-slate-300 hover:bg-slate-100 text-[11px] font-semibold inline-flex items-center justify-center gap-1"
          data-testid={`photo-download-${item.kind}-${item.id}`}
        >
          <Download className="w-3 h-3" /> Download
        </button>
      </div>
    </div>
  );
}
